// Client API dedicato al portale clienti.
// Usa axios con il token dal portal store (separato da quello admin).
import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

export const portalApi = axios.create({
  baseURL: `${BASE_URL}/api/portal`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
})

// Interceptor: aggiunge Authorization dal portal store (chiave separata)
portalApi.interceptors.request.use((config) => {
  const stored = localStorage.getItem('fc-portal-auth')
  if (stored) {
    try {
      const { state } = JSON.parse(stored) as { state: { accessToken: string | null } }
      if (state.accessToken) {
        config.headers.Authorization = `Bearer ${state.accessToken}`
      }
    } catch {
      // Ignora errori di parsing
    }
  }
  return config
})

// Interceptor risposta: redirect al login portale su 401
portalApi.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401
    ) {
      localStorage.removeItem('fc-portal-auth')
      window.location.href = '/portal/login'
    }
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------
// Tipi risposta API portale
// ---------------------------------------------------------------

export interface PortalLoginResponse {
  accessToken: string
  companyId: string
  email: string
  companyName?: string
}

export interface PortalDashboard {
  openTickets: number
  completedThisMonth: number
  nextScheduledIntervention: {
    date: string | null
    title: string | null
    ticketId: string | null
  }
  slaRespectPercent: number
  recentTickets: PortalTicketSummary[]
  recentReports: PortalReportSummary[]
  activeContracts: number
}

export interface PortalTicketSummary {
  id: string
  ticketNumber: string
  title: string
  status: string
  priority: string
  createdAt: string
}

export interface PortalReportSummary {
  id: string
  reportNumber: string
  workDescription: string | null
  status: string
  pdfUrl: string | null
  createdAt: string
}

export interface PortalTicketDetail {
  ticket: {
    id: string
    ticketNumber: string
    title: string
    description: string | null
    status: string
    priority: string
    createdAt: string
    updatedAt: string
    scheduledStart: string | null
    slaDeadline: string | null
  }
  steps: Array<{
    key: string
    label: string
    completed: boolean
    timestamp: string | null
  }>
  technicianEta: {
    available: boolean
    technicianName: string | null
    distanceKm: number | null
    etaMinutes: number | null
    lastLat: number | null
    lastLng: number | null
  }
}

export interface AvailableSlot {
  date: string
  available: boolean
}

export interface PaginatedResult<T> {
  items: T[]
  total: number
  page: number
  totalPages: number
}

// ---------------------------------------------------------------
// Funzioni API
// ---------------------------------------------------------------

export const portalApiCalls = {
  login: (data: { email: string; password?: string; token?: string }) =>
    portalApi.post<PortalLoginResponse>('/login', data),

  sendMagicLink: (email: string) =>
    portalApi.post<{ message: string }>('/magic-link', { email }),

  getDashboard: () =>
    portalApi.get<PortalDashboard>('/dashboard'),

  getTickets: (page = 1, pageSize = 20) =>
    portalApi.get<PaginatedResult<PortalTicketSummary>>('/tickets', {
      params: { page, pageSize },
    }),

  createTicket: (data: {
    siteId: string
    title: string
    description?: string
    priority?: string
  }) => portalApi.post<PortalTicketSummary>('/tickets', data),

  getTicketDetail: (id: string) =>
    portalApi.get<PortalTicketDetail>(`/tickets/${id}`),

  getReports: (params?: {
    dateFrom?: string
    dateTo?: string
    page?: number
    pageSize?: number
  }) => portalApi.get<PaginatedResult<PortalReportSummary>>('/reports', { params }),

  submitRating: (reportId: string, score: number, comment?: string) =>
    portalApi.post<{ message: string }>(`/reports/${reportId}/rating`, {
      score,
      comment,
    }),

  getAvailableSlots: (contractId: string, month: string) =>
    portalApi.get<AvailableSlot[]>(`/slots/${contractId}`, { params: { month } }),

  bookSlot: (data: {
    contractId: string
    date: string
    notes?: string
  }) => portalApi.post<{ message: string; ticket: { id: string; ticketNumber: string; scheduledStart: string } }>(
    '/slots/book',
    data
  ),
}
